import React, { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type ShareChange, type ShareSettings } from "../api.js";
import { ErrorNote, Spinner } from "../components/ui.js";
import { CopyBox } from "./CopyBox.js";
import {
  embedCode,
  linkUrl,
  startsInChoice,
  startsInOptions,
  startsInValue,
  versionNote,
  type StartsIn,
} from "./shareView.js";

type Control = "published" | "link" | "version" | "startIn" | "subtitles" | "gate";

const heading = "text-[13px] font-semibold text-ink-200";
const checkbox = "h-[18px] w-[18px] shrink-0";

/**
 * Share and embed: whether the video is public, its link and embed code, and
 * how the player behaves. Every change is saved at once and the screen takes
 * the server's answer, so what is shown is what is saved.
 *
 * Only the settings: who has watched is `ShareStats`, on the Views page. The
 * link and the embed code show only while it is live — a draft's lead nowhere.
 */
export const ShareSection: React.FC<{
  projectId: string;
  projectName: string;
  kind: "demo" | "launch";
  onLiveChange?: (live: boolean) => void;
  /** Read the settings again when this changes: an export or a new version moves what can be shared. */
  refreshKey?: string;
  /** Every answer from the server, for what sits beside this (the stats need `emailGate`). */
  onSettings?: (share: ShareSettings) => void;
}> = ({ projectId, projectName, kind, onLiveChange, refreshKey, onSettings }) => {
  const [share, setShare] = useState<ShareSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<{ control: Control; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [embedLang, setEmbedLang] = useState<string>("");
  const [confirmingLink, setConfirmingLink] = useState(false);

  const alive = useRef(true);
  const live = useRef(onLiveChange);
  live.current = onLiveChange;
  const told = useRef(onSettings);
  told.current = onSettings;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const take = useCallback((next: ShareSettings) => {
    setShare(next);
    live.current?.(next.published);
    told.current?.(next);
  }, []);

  // A new project starts from the spinner; a refresh keeps what is on screen until the answer comes.
  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    let current = true;
    if (shownFor.current !== projectId) {
      shownFor.current = projectId;
      setShare(null);
      setLoadError(null);
    }
    api
      .share(projectId)
      .then((next) => {
        if (current && alive.current) take(next);
      })
      .catch((cause: unknown) => {
        if (current && alive.current) setLoadError(cause instanceof Error ? cause.message : "Could not load sharing");
      });
    return () => {
      current = false;
    };
  }, [projectId, refreshKey, take]);

  const run = async (control: Control, call: () => Promise<ShareSettings>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await call();
      if (alive.current) take(next);
    } catch (cause) {
      const message =
        cause instanceof ApiError && cause.code === "nothing_to_publish"
          ? "Export the video first"
          : cause instanceof Error
            ? cause.message
            : "That did not save";
      if (alive.current) setError({ control, message });
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const change = (control: Control, patch: ShareChange) => run(control, () => api.updateShare(projectId, patch));
  const problem = (control: Control) =>
    error?.control === control ? (
      <p role="alert" className="text-[12px] text-signal-bad">
        {error.message}
      </p>
    ) : null;

  if (loadError) return <ErrorNote>{loadError}</ErrorNote>;
  if (!share) {
    return (
      <div className="flex justify-center p-8" role="status" aria-label="Loading share settings" aria-busy="true">
        <Spinner />
      </div>
    );
  }

  const origin = window.location.origin;
  const note = versionNote(share.version);
  const playing = share.version?.playing ?? null;
  // One select for where the player starts: the saved setting, plus a language
  // fixed in the link and the code, which this browser keeps.
  const startsIn = startsInValue(share.startIn, embedLang || null, share.languages);
  const lang = startsInChoice(startsIn).lang;
  const pickStartsIn = (value: StartsIn) => {
    const next = startsInChoice(value);
    setEmbedLang(next.lang ?? "");
    if (next.startIn !== share.startIn) void change("startIn", { startIn: next.startIn });
  };
  const on = share.published;
  const blocked = !share.canPublish && !on;

  return (
    <div role="group" aria-label={`Share and embed ${projectName}`} className="flex min-w-0 flex-col gap-[18px]">
      <div
        className={`flex flex-col gap-1 rounded-[12px] border p-3.5 ${
          // Draft in orange: a video somebody thinks is shared and is not is the thing to notice.
          on ? "border-signal-ok/30 bg-signal-ok/[0.07]" : "border-[#F5C9A8] bg-[#FFF4EC]"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[15px] font-semibold text-ink-100">{on ? "Live" : "Draft"}</span>
            <span className="text-[12.5px] leading-snug text-ink-200">
              {/* Why not, whenever it can't be published: a live share with nothing left to play says so too. */}
              {!share.canPublish
                ? `${share.reason ?? "It can't be published yet"}.`
                : on
                  ? "Anyone with the link, or on a site that embeds it, can watch."
                  : "Only your team can see it. Turn it on to get a link and embed code."}
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label="Published"
            disabled={busy || blocked}
            onClick={() => void change("published", { published: !on })}
            className={`inline-flex h-7 w-[46px] shrink-0 cursor-pointer rounded-full p-[3px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--edge-focus)] disabled:cursor-not-allowed disabled:opacity-50 ${
              on ? "justify-end bg-signal-ok" : "justify-start bg-bay-600"
            }`}
          >
            <span className="block h-[22px] w-[22px] rounded-full bg-white shadow-sm" />
          </button>
        </div>
        {problem("published")}
      </div>

      {/* The link and the code only once it is live: in a draft they lead nowhere, and copying one
          to a website before publishing would put a "not available" there. */}
      {on ? (
        <>
          <div className="flex flex-col gap-2">
            <label htmlFor="share-link" className={heading}>
              Link
            </label>
            <CopyBox
              id="share-link"
              ariaLabel="Link"
              text={linkUrl(origin, share.slug, lang)}
              buttonLabel="Copy link"
            />
            {confirmingLink ? (
              <div
                role="group"
                aria-label="Make a new link"
                className="flex flex-wrap items-center gap-2 rounded-[10px] border border-signal-bad/25 bg-signal-bad/[0.05] px-3 py-2.5"
              >
                <span className="flex-1 text-[12.5px] text-ink-200">
                  The old link and every embed of it stop working.
                </span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmingLink(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm bg-signal-bad text-white hover:bg-signal-bad/90"
                  disabled={busy}
                  onClick={() => {
                    setConfirmingLink(false);
                    void run("link", () => api.newShareLink(projectId));
                  }}
                >
                  Make new link
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="self-start py-0.5 text-[12.5px] text-ink-300 underline underline-offset-2 hover:text-ink-100 disabled:opacity-50"
                disabled={busy}
                onClick={() => setConfirmingLink(true)}
              >
                Make a new link
              </button>
            )}
            {problem("link")}
          </div>

          <div className="flex flex-col gap-2">
            <span className={heading}>Embed on a website</span>
            <CopyBox code ariaLabel="Embed code" text={embedCode(origin, share.slug, lang)} buttonLabel="Copy code" />
            <span className="text-[12px] leading-snug text-ink-300">
              Updating or adding a language updates every embed. Nothing to paste again.
            </span>
          </div>
        </>
      ) : null}

      <div className="flex flex-col gap-3 border-t border-[var(--edge)] pt-3.5">
        <span className={heading}>Player</span>

        {kind === "launch" && share.version ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <label htmlFor="share-version" className="flex-1 text-[14px] text-ink-100">
                Version shared
              </label>
              <select
                id="share-version"
                className="input !w-auto cursor-pointer"
                disabled={busy || share.version.ready.length === 0}
                value={share.version.playing?.id ?? share.version.chosen?.id ?? ""}
                onChange={(event) => void change("version", { launchVersionId: event.target.value })}
              >
                {share.version.ready.map((version) => (
                  <option key={version.id} value={version.id}>
                    v{version.number}
                  </option>
                ))}
              </select>
            </div>
            {note && playing ? (
              // The select already shows the version playing, so picking it there changes nothing.
              <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-300">
                {note}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={() => void change("version", { launchVersionId: playing.id })}
                >
                  Keep sharing v{playing.number}
                </button>
              </div>
            ) : null}
            {problem("version")}
          </div>
        ) : null}

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <label htmlFor="share-starts-in" className="flex-1 text-[14px] text-ink-100">
              Starts in
            </label>
            <select
              id="share-starts-in"
              className="input !w-auto cursor-pointer"
              disabled={busy}
              value={startsIn}
              onChange={(event) => pickStartsIn(event.target.value as StartsIn)}
            >
              {startsInOptions(share.languages).map((option) => (
                <option key={option.value} value={option.value} lang={option.lang}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {problem("startIn")}
        </div>

        <div className="flex items-start gap-3">
          <span className="flex-1 pt-1 text-[14px] text-ink-100">Languages</span>
          <div className="flex flex-wrap justify-end gap-1.5">
            {share.languages.map((language) => (
              <span
                key={language.code}
                lang={language.code}
                className="rounded-full bg-bay-800 px-2.5 py-1 text-[13px] text-ink-100"
              >
                {language.nativeName}
              </span>
            ))}
            {share.notExported.map((language) => (
              <span
                key={language.code}
                className="rounded-full border border-dashed border-[var(--edge-strong)] px-2.5 py-1 text-[13px] text-ink-300"
              >
                <span lang={language.code}>{language.nativeName}</span> · not exported
              </span>
            ))}
            {share.languages.length === 0 && share.notExported.length === 0 ? (
              <span className="pt-1 text-[12.5px] text-ink-300">None yet</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex cursor-pointer items-center gap-3 text-[14px] text-ink-100">
            <span className="flex-1">Subtitles on when it starts muted</span>
            <input
              type="checkbox"
              className={checkbox}
              disabled={busy}
              checked={share.subtitlesWhenMuted}
              onChange={(event) => void change("subtitles", { subtitlesWhenMuted: event.target.checked })}
            />
          </label>
          {problem("subtitles")}
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex cursor-pointer items-start gap-3 text-[14px] text-ink-100">
            <span className="flex flex-1 flex-col gap-0.5">
              <span>Ask for an email before it plays</span>
              <span className="text-[12px] leading-snug text-ink-300">
                Collects leads. It doesn't stop someone who watched from sharing the file.
              </span>
            </span>
            <input
              type="checkbox"
              className={`${checkbox} mt-0.5`}
              disabled={busy}
              checked={share.emailGate}
              onChange={(event) => void change("gate", { emailGate: event.target.checked })}
            />
          </label>
          {problem("gate")}
        </div>
      </div>
    </div>
  );
};
